import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { ArrowLeft, Upload, FileText, CheckCircle2, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PageHeader } from '@/components/shared'
import { parseCsv, parseExcel } from '@/lib/parseCsv'
import { emailContactService } from '@/services/emailContactService'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'

const KNOWN_FIELDS = [
  { value: 'email', label: 'Email' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'company_name', label: 'Company' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'phone', label: 'Phone' },
  { value: 'country', label: 'Country' },
  { value: 'timezone', label: 'Timezone' },
  { value: 'groups', label: 'Tags / Groups' },
  { value: 'consent_status', label: 'Consent Status' },
]

// Header aliases for auto-mapping; keys are headers lowercased with non-letters stripped
const HEADER_ALIASES = {
  email: 'email',
  emailaddress: 'email',
  firstname: 'first_name',
  givenname: 'first_name',
  fname: 'first_name',
  lastname: 'last_name',
  familyname: 'last_name',
  surname: 'last_name',
  lname: 'last_name',
  company: 'company_name',
  companyname: 'company_name',
  organization: 'company_name',
  jobtitle: 'job_title',
  designation: 'job_title',
  phone: 'phone',
  phonenumber: 'phone',
  mobile: 'phone',
  country: 'country',
  timezone: 'timezone',
  timezonename: 'timezone',
  tag: 'groups',
  tags: 'groups',
  group: 'groups',
  groups: 'groups',
  status: 'consent_status',
  consentstatus: 'consent_status',
  subscriptionstatus: 'consent_status',
  subscribed: 'consent_status',
}

// Derive a stable custom-field name from a CSV header, e.g. "PC Version" -> "pc_version"
const slugifyHeader = (header) =>
  header
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '') || 'field'

const STEPS = ['Upload', 'Map Columns', 'Review & Import']

const ContactImport = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [step, setStep] = useState(0)
  const [fileName, setFileName] = useState('')
  const [headers, setHeaders] = useState([])
  const [rows, setRows] = useState([])
  const [mapping, setMapping] = useState({})
  const [newCustomFieldColumns, setNewCustomFieldColumns] = useState(new Set())
  const [importing, setImporting] = useState(false)
  const [result, setResult] = useState(null)
  const [parsing, setParsing] = useState(false)

  const { data: fieldsData } = useQuery('email-custom-fields', emailCustomFieldService.getAllFields)
  const customFieldDefinitions = fieldsData?.data?.fields || []

  const targetOptions = [
    ...KNOWN_FIELDS,
    ...customFieldDefinitions.map((f) => ({ value: `custom:${f.name}`, label: f.label })),
    { value: 'ignore', label: 'Ignore this column' },
  ]

  const MAX_IMPORT_FILE_SIZE_BYTES = 52 * 1024 * 1024

  const applyParsedFile = (parsedHeaders, parsedRows) => {
    if (parsedHeaders.length === 0) {
      toast.error('Could not read any columns from this file')
      return
    }
    setHeaders(parsedHeaders)
    setRows(parsedRows)

    const initialMapping = {}
    parsedHeaders.forEach((header, idx) => {
      const normalized = header.toLowerCase().replace(/[^a-z]/g, '')
      initialMapping[idx] = HEADER_ALIASES[normalized] || 'ignore'
    })
    setMapping(initialMapping)
    setNewCustomFieldColumns(new Set())
    setStep(1)
  }

  const handleFile = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''

    const name = file.name.toLowerCase()
    const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls')
    const isCsv = name.endsWith('.csv')

    if (!isExcel && !isCsv) {
      toast.error('Please upload a CSV or Excel (.xlsx, .xls) file.')
      return
    }
    if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
      toast.error('File must be 52 MB or smaller.')
      return
    }

    setFileName(file.name)
    setParsing(true)
    try {
      if (isExcel) {
        const { headers: parsedHeaders, rows: parsedRows } = await parseExcel(file)
        applyParsedFile(parsedHeaders, parsedRows)
      } else {
        const text = await file.text()
        const { headers: parsedHeaders, rows: parsedRows } = parseCsv(text)
        applyParsedFile(parsedHeaders, parsedRows)
      }
    } catch (error) {
      toast.error('Could not parse this file. Please check the format and try again.')
    } finally {
      setParsing(false)
    }
  }

  const emailColumnMapped = Object.values(mapping).includes('email')

  // Columns the user opted to bring in as brand-new custom fields (not mapped to any known/existing field)
  const newCustomFields = headers
    .map((header, idx) => (newCustomFieldColumns.has(idx) ? { idx, name: slugifyHeader(header), label: header } : null))
    .filter(Boolean)
  const newCustomFieldNameByIdx = new Map(newCustomFields.map((f) => [f.idx, f.name]))

  const buildMappedRows = () => {
    return rows.map((rowValues) => {
      const record = { custom_fields: {} }
      headers.forEach((_, idx) => {
        const target = mapping[idx]
        const value = (rowValues[idx] || '').trim()
        if (!value) return
        if (newCustomFieldNameByIdx.has(idx)) {
          record.custom_fields[newCustomFieldNameByIdx.get(idx)] = value
          return
        }
        if (!target || target === 'ignore') return
        if (target.startsWith('custom:')) {
          record.custom_fields[target.slice(7)] = value
        } else if (target === 'groups') {
          record.groups = value.split(',').map((tag) => tag.trim()).filter(Boolean)
        } else {
          record[target] = value
        }
      })
      return record
    })
  }

  const mappedPreview = step === 2 ? buildMappedRows() : []
  const validRows = mappedPreview.filter((r) => r.email)
  const missingEmailCount = mappedPreview.length - validRows.length

  const handleImport = async () => {
    setImporting(true)
    try {
      const response = await emailContactService.bulkImport(mappedPreview, newCustomFields)
      setResult(response.data)
      queryClient.invalidateQueries('email-contacts')
      toast.success('Import completed')
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Import Contacts"
        description="Upload a CSV file and map its columns to contact fields"
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/contacts')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Contacts
          </Button>
        }
      />

      <div className="flex items-center gap-2 text-sm">
        {STEPS.map((label, idx) => (
          <React.Fragment key={label}>
            {idx > 0 && <div className="h-px w-8 bg-border" />}
            <div className={`flex items-center gap-1.5 ${idx === step ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${idx <= step ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                {idx + 1}
              </span>
              {label}
            </div>
          </React.Fragment>
        ))}
      </div>

      {step === 0 && (
        <Card>
          <CardContent className="p-10 flex flex-col items-center justify-center gap-4 text-center">
            {parsing ? <Loader2 className="h-10 w-10 text-muted-foreground animate-spin" /> : <Upload className="h-10 w-10 text-muted-foreground" />}
            <div>
              <p className="font-medium">Choose a CSV or Excel file to import</p>
              <p className="text-sm text-muted-foreground">Accepts .csv, .xlsx, .xls — the first row should contain column headers.</p>
            </div>
            <Button asChild disabled={parsing}>
              <label className={parsing ? 'cursor-not-allowed' : 'cursor-pointer'}>
                {parsing ? 'Parsing…' : 'Select File'}
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  className="hidden"
                  disabled={parsing}
                  onChange={handleFile}
                />
              </label>
            </Button>
          </CardContent>
        </Card>
      )}

      {step === 1 && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              {fileName} — {rows.length} rows, {headers.length} columns
            </div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="bg-muted/50 font-medium">CSV Column</TableHead>
                  <TableHead className="bg-muted/50 font-medium">Sample Value</TableHead>
                  <TableHead className="bg-muted/50 font-medium">Maps To</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {headers.map((header, idx) => (
                  <TableRow key={idx} className="border-border">
                    <TableCell className="font-medium">{header}</TableCell>
                    <TableCell className="text-muted-foreground">{rows[0]?.[idx] || '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1.5">
                        <Select
                          value={mapping[idx] || 'ignore'}
                          disabled={newCustomFieldColumns.has(idx)}
                          onValueChange={(value) => setMapping((prev) => ({ ...prev, [idx]: value }))}
                        >
                          <SelectTrigger className="w-56">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {targetOptions.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {(mapping[idx] || 'ignore') === 'ignore' && (
                          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                            <Checkbox
                              checked={newCustomFieldColumns.has(idx)}
                              onCheckedChange={(checked) => {
                                setNewCustomFieldColumns((prev) => {
                                  const next = new Set(prev)
                                  if (checked) next.add(idx)
                                  else next.delete(idx)
                                  return next
                                })
                              }}
                            />
                            Add as custom field
                          </label>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!emailColumnMapped && (
              <p className="text-sm text-destructive">Map at least one column to "Email" to continue.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(0)}>Back</Button>
              <Button disabled={!emailColumnMapped} onClick={() => setStep(2)}>Continue</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && !result && (
        <Card>
          <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Total Rows</p>
                <p className="text-xl font-semibold">{mappedPreview.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Ready</p>
                <p className="text-xl font-semibold text-emerald-600">{validRows.length}</p>
              </div>
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Missing Email</p>
                <p className="text-xl font-semibold text-destructive">{missingEmailCount}</p>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="bg-muted/50 font-medium">Email</TableHead>
                  <TableHead className="bg-muted/50 font-medium">Name</TableHead>
                  <TableHead className="bg-muted/50 font-medium">Company</TableHead>
                  <TableHead className="bg-muted/50 font-medium">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappedPreview.slice(0, 20).map((r, idx) => (
                  <TableRow key={idx} className="border-border">
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell>{[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                    <TableCell>{r.company_name || '—'}</TableCell>
                    <TableCell>
                      {r.email ? <Badge variant="secondary">Ready</Badge> : <Badge variant="outline" className="text-destructive">Missing email</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {mappedPreview.length > 20 && (
              <p className="text-xs text-muted-foreground">Showing first 20 of {mappedPreview.length} rows.</p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button disabled={validRows.length === 0 || importing} onClick={handleImport}>
                {importing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {validRows.length} Contact{validRows.length === 1 ? '' : 's'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardContent className="p-10 flex flex-col items-center gap-4 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <div>
              <p className="font-medium">Import complete</p>
              <p className="text-sm text-muted-foreground">
                {result.created} created, {result.updated} updated, {result.skipped} skipped
                {result.errors?.length > 0 && `, ${result.errors.length} errors`}
              </p>
            </div>
            <Button onClick={() => navigate('/marketing-email/contacts')}>Go to Contacts</Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

export default ContactImport
